import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import {
  GridCallbackDetails,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useDebounce } from '@uidotdev/usehooks';

import { DEFAULT_PAGE } from '@/data/constants';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

import { SortModel } from '@/data/models/sort-model.model';
import { TripLegExceptionBC } from '@/data/models/trip-leg-exception.model';
import { FilterModel } from '@/data/models/filters.model';

import { TripLegEventType } from '@/services/webSocketService/updateTripLeg';
import realtimeUtils from '@/services/webSocketService/realtimeUtils';

import * as lookupSlice from '@/data/lookup.slice';
import {
  fetchMonitoringExceptionsAsync,
  removeTripLegEventFromQueue,
  selectAllTripLegs,
  selectLoadingMonitoringExceptions,
  selectMonitoringExceptions,
  selectTripLegEventQueue,
} from '@/data/slices/trip-leg.slice';

import { MonitoringExceptionsTable } from './MonitoringExceptionsTable';
import { TripLegSearchMenu } from '@/data/requests/trip-legs';

const PREFIX = 'MonitoringExceptions';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  filter: `${PREFIX}-filter`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3), // Adds bottom margin to all child components except the last one
    },
    boxShadow: '0px 0px 10px #dfdfdf',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
    flexGrow: 1,
  },
  [`& .${classes.filter}`]: {
    width: 200,
  },
}));

const EXCEPTION_REFRESH_EVENT_TYPES = [
  TripLegEventType.TripLegException,
  TripLegEventType.TripLegExceptionAssigned,
  TripLegEventType.TripLegExceptionCleared,
];

export default function MonitoringExceptions() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const FILTER_ALL = [
    TripLegExceptionBC.DriverLate,
    TripLegExceptionBC.OpenSameDay,
    TripLegExceptionBC.NoDriver,
    TripLegExceptionBC.DriverNotOnline,
    TripLegExceptionBC.EtaNotUpdating,
  ];
  const [currentFilter, setCurrentFilter] = useState<string[]>(FILTER_ALL);
  const [page, setPage] = useState(DEFAULT_PAGE);
  // Deviate from standard page size for this page specifically F1V2-3749
  const [pageSize, setPageSize] = useState(100);
  const [sortModel, setSortModel] = useState<SortModel>({
    column: 'appointmentDate,pickUpDueTimeUTC',
    direction: 'asc,asc',
  });
  const [refetchTripLegs, setRefetchTripLegs] = useState(false);
  const debouncedRefetchTripLegs = useDebounce(refetchTripLegs, 200);

  const tripLegEventQueue = useAppSelector(selectTripLegEventQueue);
  const allTripLegs = useAppSelector(selectAllTripLegs);
  const monitoringExceptions = useAppSelector(selectMonitoringExceptions);
  const loadingMonitoringExceptions = useAppSelector(
    selectLoadingMonitoringExceptions,
  );

  const sortedTripLegs = useMemo(() => {
    return [...monitoringExceptions.items].sort(
      (a, b) => Number(Boolean(a.isWillCall)) - Number(Boolean(b.isWillCall)),
    );
  }, [monitoringExceptions.items]);

  const filterOptions = [
    { label: t('triplegInventory.filterAll'), value: FILTER_ALL },
    {
      label: t('tripLegException.open'),
      value: [TripLegExceptionBC.OpenSameDay],
    },
    {
      label: t('tripLegException.driverLate'),
      value: [TripLegExceptionBC.DriverLate],
    },
    {
      label: t('tripLegException.noDriver'),
      value: [TripLegExceptionBC.NoDriver],
    },
    {
      label: t('tripLegException.driverNotOnline'),
      value: [TripLegExceptionBC.DriverNotOnline],
    },
    {
      label: t('tripLegException.etaNotUpdating'),
      value: [TripLegExceptionBC.EtaNotUpdating],
    },
  ];

  const handlePaginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== monitoringExceptions.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const handleTripLegFilterChange = (event: any) => {
    const f = event.target.value;
    setCurrentFilter(f?.split(','));
  };

  const handleSortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    }
    if (newModel) {
      setPage(1);
      setSortModel(newModel);
    } else {
      setPage(1);
      setSortModel({
        column: 'appointmentDate,pickUpDueTimeUTC',
        direction: 'asc,asc',
      });
    }
  };

  const activeSearchRequestRef = useRef<AbortController | null>(null);
  const hasFetchedInitialDataRef = useRef(false);
  const fetchTripsWithExceptions = useCallback(() => {
    activeSearchRequestRef.current?.abort();
    const abortController = new AbortController();
    activeSearchRequestRef.current = abortController;

    const today = DateTime.now().toISODate();
    const syncIdsOnly = hasFetchedInitialDataRef.current;
    hasFetchedInitialDataRef.current = true;

    dispatch(
      fetchMonitoringExceptionsAsync({
        page,
        pageSize,
        sort: sortModel?.column ?? undefined,
        sortOrder: sortModel?.direction ?? undefined,
        menu: TripLegSearchMenu.MONITORING_EXCEPTIONS,
        pickupDateFrom: today,
        pickupDateTo: today,
        tripLegExceptionBCs: currentFilter,
        todayDate: DateTime.local().toFormat('yyyy-MM-dd'),
        syncIdsOnly,
        signal: abortController.signal,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, currentFilter]);

  useEffect(() => {
    if (debouncedRefetchTripLegs) {
      fetchTripsWithExceptions();
      setRefetchTripLegs(false);
    }
  }, [debouncedRefetchTripLegs]);

  // General refetch of page for pagination, sort, and filter changes.
  useEffect(() => {
    setRefetchTripLegs(true);
  }, [fetchTripsWithExceptions, page, pageSize, sortModel, currentFilter]);

  /*
   * Real time updates for exceptions, exception assignments, and other
   * events that affect the data on this page.
   */
  useEffect(() => {
    if (allTripLegs.length > 0 && tripLegEventQueue.length > 0) {
      const lastEvent = tripLegEventQueue[tripLegEventQueue.length - 1];
      if (
        EXCEPTION_REFRESH_EVENT_TYPES.includes(
          lastEvent.detailType as TripLegEventType,
        )
      ) {
        const shouldRefresh = realtimeUtils.shouldRefreshTripLegData({
          tripLegEventType: lastEvent.detailType as TripLegEventType,
          tripLegEvent: lastEvent.detail,
          tripLegs: monitoringExceptions.items,
          allTripLegs,
          filters: {
            ...monitoringExceptions.filters,
            sort: sortModel.column,
            sortOrder: sortModel.direction,
          } as FilterModel,
        });
        if (shouldRefresh) {
          setRefetchTripLegs(true);
        }
      }
      dispatch(
        removeTripLegEventFromQueue({
          detailType: lastEvent.detailType as TripLegEventType,
          detail: lastEvent.detail,
        }),
      );
    }
  }, [allTripLegs, fetchTripsWithExceptions, dispatch]);

  useEffect(() => {
    dispatch(
      lookupSlice.fetchLookupItemsAsync('transportationProviderPhoneUse'),
    );
  }, []);

  return (
    <StyledBox className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('monitoring.exceptions')}
        </Typography>
        <TextField
          select
          value={currentFilter}
          className={classes.filter}
          required={false}
          onChange={handleTripLegFilterChange}
          label={t('tripLegException.exception')}
          variant="outlined">
          {filterOptions.map((opt) => {
            return (
              <MenuItem value={opt.value.join(',')} key={opt.value.join(',')}>
                {opt.label}
              </MenuItem>
            );
          })}
        </TextField>
      </div>
      <MonitoringExceptionsTable
        page={page}
        pageSize={pageSize}
        rowCount={monitoringExceptions.itemCount}
        handlePaginationChange={handlePaginationChange}
        handleSortModelChange={handleSortModelChange}
        tripLegs={sortedTripLegs}
        loading={loadingMonitoringExceptions}
      />
    </StyledBox>
  );
}
